import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  CalendarDays,
  BookOpen,
  BedDouble,
  LayoutList,
  Users,
  FileText,
  BarChart2,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  MessageSquare,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils/cn";
import { useAuth } from "@/hooks/useAuth";
import { useUnreadCount } from "@/features/admin-enquiries/hooks/useEnquiries";

interface NavItem {
  label: string;
  href: string;
  icon: React.ElementType;
  superOnly?: boolean;
  end?: boolean;
  badge?: number;
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

function SidebarNav({
  groups,
  collapsed,
  onClose,
}: {
  groups: NavGroup[];
  collapsed: boolean;
  onClose: () => void;
}) {
  return (
    <div className="space-y-4 px-2">
      {groups.map((group) => (
        <div key={group.title}>
          {!collapsed && (
            <p className="mb-1 px-3 text-[10px] font-semibold uppercase tracking-widest text-white/30">
              {group.title}
            </p>
          )}
          {collapsed && <div className="mx-3 my-1 h-px bg-white/10" />}
          <ul className="space-y-0.5">
            {group.items.map((item) => (
              <li key={item.href}>
                <NavLink
                  to={item.href}
                  end={item.end}
                  onClick={onClose}
                  className={({ isActive }) =>
                    cn(
                      "group relative flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition",
                      isActive
                        ? "bg-white/10 text-white"
                        : "text-white/60 hover:bg-white/[0.08] hover:text-white/90",
                      collapsed && "justify-center px-2",
                    )
                  }
                  title={collapsed ? item.label : undefined}
                >
                  {({ isActive }) => (
                    <>
                      {/* Active left border indicator */}
                      {isActive && (
                        <span className="absolute left-0 top-1/2 h-5 w-0.5 -translate-y-1/2 rounded-full bg-metro-gold" />
                      )}

                      <item.icon
                        size={18}
                        strokeWidth={isActive ? 2 : 1.5}
                        className={cn(
                          "shrink-0 transition",
                          isActive ? "text-metro-gold" : "text-current",
                        )}
                      />

                      {!collapsed && (
                        <span className="flex-1">{item.label}</span>
                      )}

                      {/* Badge — expanded */}
                      {!collapsed && item.badge != null && item.badge > 0 && (
                        <span className="rounded-full bg-metro-gold px-1.5 py-0.5 text-[10px] font-bold leading-none text-metro-green-950">
                          {item.badge > 99 ? "99+" : item.badge}
                        </span>
                      )}

                      {/* Badge — collapsed */}
                      {collapsed && item.badge != null && item.badge > 0 && (
                        <span className="absolute right-1 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-metro-gold text-[9px] font-bold text-metro-green-950">
                          {item.badge > 99 ? "9+" : item.badge}
                        </span>
                      )}
                    </>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

interface AdminSidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  onClose: () => void;
}

export default function AdminSidebar({
  collapsed,
  onToggle,
  onClose,
}: AdminSidebarProps) {
  const { user, signOut, isSuperAdmin } = useAuth();
  const unreadEnquiries = useUnreadCount();

  const NAV_GROUPS: NavGroup[] = [
    {
      title: "Operations",
      items: [
        {
          label: "Dashboard",
          href: "/admin",
          icon: LayoutDashboard,
          end: true,
        },
        { label: "Calendar", href: "/admin/calendar", icon: CalendarDays },
        { label: "Bookings", href: "/admin/bookings", icon: BookOpen },
      ],
    },
    {
      title: "Guests",
      items: [
        { label: "Guests", href: "/admin/guests", icon: Users },
        {
          label: "Enquiries",
          href: "/admin/enquiries",
          icon: MessageSquare,
          badge: unreadEnquiries,
        },
      ],
    },
    {
      title: "Inventory",
      items: [
        {
          label: "Room Types",
          href: "/admin/rooms/types",
          icon: LayoutList,
          superOnly: true,
        },
        { label: "Rooms", href: "/admin/rooms", icon: BedDouble, end: true },
      ],
    },
    {
      title: "Manage",
      items: [
        {
          label: "Content",
          href: "/admin/content",
          icon: FileText,
          superOnly: true,
        },
        { label: "Reports", href: "/admin/reports", icon: BarChart2 },
        {
          label: "Settings",
          href: "/admin/settings",
          icon: Settings,
          superOnly: true,
        },
      ],
    },
  ];

  const visibleGroups = NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => !item.superOnly || isSuperAdmin),
  })).filter((group) => group.items.length > 0);

  return (
    <aside
      className={cn(
        "relative flex h-screen flex-col bg-metro-green-950 transition-all duration-300",
        collapsed ? "w-[68px]" : "w-[220px]",
      )}
    >
      {/* Logo + close button */}
      <div className="flex h-16 items-center justify-between border-b border-white/10 px-4">
        {!collapsed && (
          <span className="font-serif text-lg tracking-[0.18em] text-white">
            METROPOLIS
          </span>
        )}
        <div className="flex items-center gap-1">
          {/* Mobile close button */}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close menu"
            className="flex h-7 w-7 items-center justify-center rounded-md text-white/50 transition hover:bg-white/10 hover:text-white lg:hidden"
          >
            <X size={16} />
          </button>

          {/* Desktop collapse toggle */}
          <button
            type="button"
            onClick={onToggle}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className={cn(
              "hidden h-7 w-7 items-center justify-center rounded-md text-white/50 transition hover:bg-white/10 hover:text-white lg:flex",
              collapsed && "mx-auto",
            )}
          >
            {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-4">
        <SidebarNav
          groups={visibleGroups}
          collapsed={collapsed}
          onClose={onClose}
        />
      </nav>

      {/* User + sign out */}
      <div className="border-t border-white/10 p-3">
        {!collapsed && user && (
          <div className="mb-2 px-2">
            <p className="truncate text-xs font-medium text-white/80">
              {user.displayName}
            </p>
            <p className="truncate text-[10px] capitalize text-white/40">
              {user.role.replace("_", " ")}
            </p>
          </div>
        )}
        <button
          type="button"
          onClick={signOut}
          className={cn(
            "flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-white/50 transition hover:bg-white/10 hover:text-white",
            collapsed && "justify-center px-2",
          )}
          title={collapsed ? "Sign out" : undefined}
        >
          <LogOut size={16} className="shrink-0" />
          {!collapsed && <span>Sign out</span>}
        </button>
      </div>
    </aside>
  );
}